/**
 * BatchMatrixRow - Wrapper with collapsible logic
 *
 * Combines BatchMatrixRowCompact and BatchMatrixRowExpanded.
 */

import { useState, useMemo, useEffect } from 'react';
import { BatchMatrixRowCompact } from './BatchMatrixRowCompact';
import { BatchMatrixRowExpanded } from './BatchMatrixRowExpanded';
import type { MatrixItem, BatchMode } from '@/lib/api/batch';
import { getCollection } from '@/lib/api/template-collections';

interface Template {
  id: string;
  name: string;
}

interface Collection {
  id: string;
  name: string;
}

interface BatchMatrixRowProps {
  item: MatrixItem;
  index: number;
  templates: Template[];
  collections: Collection[];
  collectionCountCache: Map<string, number>;
  cacheCollectionCount: (id: string, count: number) => void;
  onChange: (item: MatrixItem) => void;
  onRemove: () => void;
  onDuplicateRow: () => void;
  onSelectFile: () => void;
  onSelectFromGallery: () => void;
  canRemove: boolean;
  disabled?: boolean;
}

export function BatchMatrixRow({
  item,
  index,
  templates,
  collections,
  collectionCountCache,
  cacheCollectionCount,
  onChange,
  onRemove,
  onDuplicateRow,
  onSelectFile,
  onSelectFromGallery,
  canRemove,
  disabled = false,
}: BatchMatrixRowProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const itemMode: BatchMode = item.mode || 'template';
  const collectionId = item.collection_id;

  // Fetch collection image count when collection changes
  useEffect(() => {
    if (itemMode !== 'collection' || !collectionId) return;
    if (collectionCountCache.has(collectionId)) return;

    getCollection(collectionId).then((data) => {
      if (data?.items) {
        const total = data.items.reduce((sum: number, ci: { image_count?: number }) => sum + (ci.image_count || 1), 0);
        cacheCollectionCount(collectionId, total);
      }
    }).catch(() => { /* ignore */ });
  }, [itemMode, collectionId, collectionCountCache, cacheCollectionCount]);

  const collectionImageCount = useMemo(() => {
    if (itemMode === 'collection' && collectionId) {
      return collectionCountCache.get(collectionId);
    }
    return undefined;
  }, [itemMode, collectionId, collectionCountCache]);

  return (
    <div className="space-y-0">
      <BatchMatrixRowCompact
        item={item}
        index={index}
        templates={templates}
        collections={collections}
        collectionImageCount={collectionImageCount}
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded(!isExpanded)}
        onRemove={onRemove}
        onDuplicate={onDuplicateRow}
        onChange={onChange}
        onSelectFile={onSelectFile}
        onSelectFromGallery={onSelectFromGallery}
        canRemove={canRemove}
        disabled={disabled}
      />

      {isExpanded && (
        <BatchMatrixRowExpanded
          item={item}
          templates={templates}
          collections={collections}
          onChange={onChange}
          onSelectFile={onSelectFile}
          onSelectFromGallery={onSelectFromGallery}
          disabled={disabled}
        />
      )}
    </div>
  );
}
